import type { OverrideAdapter } from '@statsig/react-bindings';

/**
 * Builds a Statsig override adapter that forces the given gates ON, for local
 * development. Set a comma-separated list of gate names in `.env.local`:
 *
 *   VITE_STATSIG_GATE_OVERRIDES=enable_integration_assistant
 *
 * Returns `undefined` when nothing is configured, so it has no effect in
 * staging/production builds (where the env var is unset) and the real Statsig
 * evaluation is used unchanged.
 *
 * @param raw - The raw comma-separated env value (or undefined).
 * @returns An OverrideAdapter forcing the listed gates true, or undefined.
 */
export function buildLocalGateOverrideAdapter(
  raw: string | undefined,
): OverrideAdapter | undefined {
  const gates = new Set(
    (raw ?? '')
      .split(',')
      .map(name => name.trim())
      .filter(Boolean),
  );
  if (gates.size === 0) return undefined;

  return {
    getGateOverride: current =>
      gates.has(current.name) ? { ...current, value: true } : null,
  };
}

/**
 * localStorage key an e2e spec sets (comma-separated gate names) to force
 * gates ON for its page, whatever Statsig's dev rules say.
 */
export const E2E_GATE_OVERRIDES_STORAGE_KEY = 'e2e_statsig_gate_overrides';

/**
 * Reads the gates an e2e spec forces ON. Only in an e2e build
 * (`VITE_E2E_MODE`), so a real session can't turn gates on from localStorage.
 *
 * @param isE2eMode - Whether the app runs under Playwright.
 * @returns The comma-separated gate names, or undefined when none are set.
 */
export function readE2eGateOverrides(isE2eMode: boolean): string | undefined {
  if (!isE2eMode) return undefined;
  try {
    return localStorage.getItem(E2E_GATE_OVERRIDES_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}
